import * as React from "react";
import Map, {
  FullscreenControl,
  ScaleControl,
  Popup,
} from "react-map-gl/mapbox";
import { useSelector } from "react-redux";
import { CONFIG } from "../../config";
import { getMapStyle, MAP_STYLES } from "../../utils/getMapStyle";

import { useNavigate } from "react-router-dom";
import { useMapControls } from "../../hooks/useMapControls";
import mapboxgl from "mapbox-gl";
import { withMapErrorBoundary } from "../ErrorBoundaries";
import { useTheme } from "@mui/system";
import { useDevices } from "../../hooks/queries/useDevices";
import {
  useGeofences,
  useGeofencePresenceHistory,
} from "../../hooks/queries/useGeofences";
import { useTranslation } from "react-i18next";
import { usePerformanceOptimizations } from "../../hooks/usePerformanceOptimizations";
import { useGeofenceOverlay } from "../../hooks/useGeofenceOverlay";
import { useMapControlSetup } from "../../hooks/useMapControlSetup";
import { VirtualizedDeviceMarkers } from "./components/VirtualizedDeviceMarkers";
import {
  buildDevicePopupContent,
  buildGeofencePopupContent,
} from "./components/MapPopup";
import MapPopupActionButton from "./components/MapPopupActionButton";
import GeofenceOverlayLayers from "./components/GeofenceOverlayLayers";
import DeviceClusterLayers from "./components/DeviceClusterLayers";
import MapStyleControl from "./components/MapStyleControl";
import { isDeviceOutdatedByTime } from "../../utils/deviceStatus";
import { getDeviceFreshnessTime } from "../../utils/deviceUtils";
import {
  getMapboxMap,
  isValidCoordinate,
  getBoundsFromGeometry,
} from "../../utils/mapHelpers";

mapboxgl.accessToken = CONFIG.mapboxAccessToken;

const CLUSTER_THRESHOLD = 200; // Only cluster if more than this many devices

// Memoized DevicesMap component to prevent unnecessary re-renders
const DevicesMap = React.memo(({ devices: propDevices }) => {
  // Redux state (for UI state)
  const organisation = useSelector((state) => state.selectedOrganisation);
  const pitch = useSelector((state) => state.pitch);

  // TanStack Query data - use prop devices if provided, otherwise fetch
  const { data: fetchedDevices = [] } = useDevices(organisation?.id);
  const devices = propDevices ?? fetchedDevices;
  const { data: geofences = [] } = useGeofences(organisation?.id);

  const navigate = useNavigate();
  const theme = useTheme();

  const { t } = useTranslation("common");

  // Performance optimizations
  const { debounce, throttleRAF, cleanup } = usePerformanceOptimizations();

  const mapRef = React.useRef(null);
  const popupCloseHandlerRef = React.useRef(null);
  const geofenceClickedRef = React.useRef(false);
  const [mapLoaded, setMapLoaded] = React.useState(false);
  const [showPopup, setShowPopup] = React.useState(false);
  const [selectedDevice, setSelectedDevice] = React.useState(null);
  const [selectedGeofence, setSelectedGeofence] = React.useState(null);
  const { data: selectedGeofenceHistory } = useGeofencePresenceHistory(
    selectedGeofence?.id,
    0,
    5
  );

  // Cleanup performance optimizations on unmount
  React.useEffect(() => {
    return cleanup;
  }, [cleanup]);

  const { filteredDevices, fitToHistory, initialViewState } = useMapControls(
    devices,
    organisation,
    mapRef,
    pitch
  );

  const { attachControls, cleanupControls } = useMapControlSetup({
    pitch,
    debounce,
  });

  const { geofenceSource, geofencePinSource } = useGeofenceOverlay(
    geofences,
    filteredDevices
  );

  const handleMapLoad = React.useCallback(
    (e) => {
      mapRef.current = e.target;
      setMapLoaded(true);
      const map = getMapboxMap(mapRef);

      if (!map) {
        return;
      }

      attachControls(map);

      // Debounced map operations for better performance
      const debouncedMapSetup = debounce(
        () => {
          if (
            organisation.hero === "map" &&
            organisation.centerandzoom !== "false"
          ) {
            map.flyTo({
              center: organisation.center.split(",").map(Number),
              zoom: organisation.zoom,
              animate: false,
              pitch: pitch,
            });
          } else {
            fitToHistory();
          }
        },
        100,
        "mapSetup"
      );

      debouncedMapSetup();

      // Throttled popup close handler for better responsiveness
      const throttledPopupClose = throttleRAF(() => {
        if (geofenceClickedRef.current) {
          geofenceClickedRef.current = false;
          return;
        }
        setShowPopup(false);
        setSelectedGeofence(null);
      }, "popupClose");

      if (popupCloseHandlerRef.current) {
        map.off("click", popupCloseHandlerRef.current);
      }
      popupCloseHandlerRef.current = throttledPopupClose;
      map.on("click", throttledPopupClose);
    },
    [organisation, fitToHistory, pitch, debounce, throttleRAF, attachControls]
  );

  React.useEffect(() => {
    return () => {
      const map = getMapboxMap(mapRef);
      if (map && popupCloseHandlerRef.current) {
        map.off("click", popupCloseHandlerRef.current);
      }
      cleanupControls(map);
      setMapLoaded(false);
      popupCloseHandlerRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const flyToDevice = React.useCallback(
    (device) => {
      if (!isValidCoordinate(device?.lng, device?.lat)) {
        return;
      }

      // Debounced flyTo operation for smoother performance
      const debouncedFlyTo = debounce(
        () => {
          const lng = Number(device.lng);
          const lat = Number(device.lat);
          const map = getMapboxMap(mapRef);

          if (!map) {
            return;
          }

          map.flyTo({
            center: [lng, lat],
            zoom: 15,
            animate: true,
            pitch: pitch,
            duration: 1000, // Reduced from default 2000ms for faster response
          });

          // Throttled pitch setting
          const throttledPitchSet = throttleRAF(() => {
            if (typeof pitch === "number") {
              map.setPitch(pitch);
            }
          }, "flyToPitch");

          setTimeout(throttledPitchSet, 100);

          setSelectedDevice(device);
          setShowPopup(true);
        },
        50,
        "flyToDevice"
      );

      debouncedFlyTo();
    },
    [pitch, debounce, throttleRAF]
  );

  const isDeviceOutdated = React.useCallback(
    (freshnessTime) => isDeviceOutdatedByTime(freshnessTime),
    []
  );

  const popupContent = React.useMemo(() => {
    if (!selectedDevice) {
      return "";
    }

    return buildDevicePopupContent(selectedDevice, theme, {
      fallbackTitle: t("common.device", { defaultValue: "Device" }),
    });
  }, [selectedDevice, theme, t]);

  const geofencePopupContent = React.useMemo(() => {
    if (!selectedGeofence) return "";
    return buildGeofencePopupContent({
      geofence: selectedGeofence,
      deviceCount: selectedGeofence.count,
      history: selectedGeofenceHistory,
      devices,
      theme,
    });
  }, [selectedGeofence, selectedGeofenceHistory, devices, theme]);

  // Memoized map style to prevent re-renders
  const mapStyle = React.useMemo(
    () => getMapStyle(theme.palette.mode, MAP_STYLES.branded),
    [theme.palette.mode]
  );

  // Determine if clustering should be enabled
  const shouldCluster = filteredDevices.length > CLUSTER_THRESHOLD;

  // Create GeoJSON source for clustered devices
  const devicesGeoJson = React.useMemo(
    () => ({
      type: "FeatureCollection",
      features: filteredDevices
        .filter((device) => isValidCoordinate(device?.lng, device?.lat))
        .map((device) => ({
          type: "Feature",
          properties: {
            id: device.id,
            name: device.name,
            last_heard: device.last_heard,
            last_location: device.last_location,
            isOutdated: isDeviceOutdated(getDeviceFreshnessTime(device)),
          },
          geometry: {
            type: "Point",
            coordinates: [parseFloat(device.lng), parseFloat(device.lat)],
          },
        })),
    }),
    [filteredDevices, isDeviceOutdated]
  );

  const selectedCoords = React.useMemo(() => {
    if (
      !selectedDevice ||
      !isValidCoordinate(selectedDevice.lng, selectedDevice.lat)
    ) {
      return null;
    }

    return {
      longitude: Number(selectedDevice.lng),
      latitude: Number(selectedDevice.lat),
    };
  }, [selectedDevice]);

  // Handle cluster click - zoom into the cluster
  const handleClusterClick = React.useCallback(
    (e) => {
      const features = e.features;
      if (!features || !features.length) return;

      const feature = features[0];
      const clusterId = feature.properties.cluster_id;

      if (!clusterId) {
        // It's a single point, not a cluster
        const deviceId = feature.properties.id;
        const device = filteredDevices.find((d) => d.id === deviceId);
        if (device) {
          flyToDevice(device);
        }
        return;
      }

      // Get the cluster expansion zoom
      const map = getMapboxMap(mapRef);
      const mapboxSource = map?.getSource("devices-clustered");
      if (map && mapboxSource) {
        mapboxSource.getClusterExpansionZoom(clusterId, (err, zoom) => {
          if (err) return;

          map.flyTo({
            center: feature.geometry.coordinates,
            zoom: zoom,
            duration: 500,
          });
        });
      }
    },
    [filteredDevices, flyToDevice]
  );

  // Add click handlers for cluster layers
  React.useEffect(() => {
    const map = getMapboxMap(mapRef);
    if (!mapLoaded || !map || !shouldCluster) return;

    const onClusterClick = (e) => handleClusterClick(e);
    const onClustersMouseEnter = () => {
      map.getCanvas().style.cursor = "pointer";
    };
    const onClustersMouseLeave = () => {
      map.getCanvas().style.cursor = "";
    };
    const onUnclusteredMouseEnter = () => {
      map.getCanvas().style.cursor = "pointer";
    };
    const onUnclusteredMouseLeave = () => {
      map.getCanvas().style.cursor = "";
    };

    map.on("click", "clusters", onClusterClick);
    map.on("click", "unclustered-point", onClusterClick);

    // Change cursor on hover
    map.on("mouseenter", "clusters", onClustersMouseEnter);
    map.on("mouseleave", "clusters", onClustersMouseLeave);
    map.on("mouseenter", "unclustered-point", onUnclusteredMouseEnter);
    map.on("mouseleave", "unclustered-point", onUnclusteredMouseLeave);

    return () => {
      map.off("click", "clusters", onClusterClick);
      map.off("click", "unclustered-point", onClusterClick);
      map.off("mouseenter", "clusters", onClustersMouseEnter);
      map.off("mouseleave", "clusters", onClustersMouseLeave);
      map.off("mouseenter", "unclustered-point", onUnclusteredMouseEnter);
      map.off("mouseleave", "unclustered-point", onUnclusteredMouseLeave);
    };
  }, [mapLoaded, shouldCluster, handleClusterClick]);

  // Geofence click → navigate to detail; cursor pointer on hover
  React.useEffect(() => {
    const map = getMapboxMap(mapRef);
    if (!mapLoaded || !map) return;

    const onGeofenceClick = (e) => {
      const feature = e.features?.[0];
      if (!feature) return;
      const { geofence_id, geofence_name, device_count } = feature.properties;
      if (geofence_id) {
        geofenceClickedRef.current = true;

        // Pins are Point geometry — look up the actual polygon to get real bounds
        let boundsGeometry = feature.geometry;
        if (feature.geometry.type === "Point") {
          const polygonFeatures = map.querySourceFeatures("geofences", {
            filter: ["==", ["get", "geofence_id"], geofence_id],
          });
          if (polygonFeatures.length > 0) {
            boundsGeometry = polygonFeatures[0].geometry;
          }
        }

        const bounds = getBoundsFromGeometry(boundsGeometry);
        if (bounds) {
          map.fitBounds(bounds, {
            padding: 60,
            duration: 800,
            maxZoom: 16,
            pitch: map.getPitch(),
            bearing: map.getBearing(),
          });
        }
        setSelectedGeofence({
          id: geofence_id,
          name: geofence_name,
          count: device_count || 0,
          lng: e.lngLat.lng,
          lat: e.lngLat.lat,
        });
      }
    };
    const onEnter = () => {
      map.getCanvas().style.cursor = "pointer";
    };
    const onLeave = () => {
      map.getCanvas().style.cursor = "";
    };

    const layers = ["geofence-pins", "geofence-labels"];
    layers.forEach((layer) => {
      map.on("click", layer, onGeofenceClick);
      map.on("mouseenter", layer, onEnter);
      map.on("mouseleave", layer, onLeave);
    });

    return () => {
      layers.forEach((layer) => {
        map.off("click", layer, onGeofenceClick);
        map.off("mouseenter", layer, onEnter);
        map.off("mouseleave", layer, onLeave);
      });
    };
  }, [mapLoaded]);

  return (
    <>
      <Map
        mapboxAccessToken={CONFIG.mapboxAccessToken}
        mapStyle={mapStyle}
        onLoad={handleMapLoad}
        initialViewState={{ ...initialViewState, pitch }}
        id="map-panel"
      >
        {/* Navigation control is added programmatically in handleMapLoad */}
        <FullscreenControl position="top-left" />
        <ScaleControl position="bottom-right" />
        <MapStyleControl roadmapStyle={MAP_STYLES.branded} />

        {/* Geofence layers - bottom layer */}
        <GeofenceOverlayLayers
          geofenceSource={geofenceSource}
          geofencePinSource={geofencePinSource}
          primaryColor={theme.palette.primary.main}
          themeMode={theme.palette.mode}
        />

        {/* Device markers - use clustering when there are many devices */}
        {shouldCluster ? (
          <DeviceClusterLayers data={devicesGeoJson} />
        ) : (
          <VirtualizedDeviceMarkers
            devices={filteredDevices}
            mapRef={mapRef}
            mapLoaded={mapLoaded}
            isDeviceOutdated={isDeviceOutdated}
            onDeviceClick={flyToDevice}
            maxMarkers={1000}
          />
        )}

        {showPopup && selectedDevice && selectedCoords && (
          <Popup
            anchor="bottom"
            latitude={selectedCoords.latitude}
            longitude={selectedCoords.longitude}
            onClose={() => setShowPopup(false)}
            closeOnClick={false}
            closeButton={true}
            maxWidth="320px"
            offset={[0, -10]}
          >
            <>
              <div dangerouslySetInnerHTML={{ __html: popupContent }} />
              <MapPopupActionButton
                to={`/device/${selectedDevice.id}`}
                label={t("common.view_device", { defaultValue: "View Device" })}
              />
            </>
          </Popup>
        )}

        {selectedGeofence && (
          <Popup
            anchor="bottom"
            latitude={selectedGeofence.lat}
            longitude={selectedGeofence.lng}
            onClose={() => setSelectedGeofence(null)}
            closeOnClick={false}
            closeButton={true}
            maxWidth="280px"
            offset={[0, -10]}
          >
            <>
              <div dangerouslySetInnerHTML={{ __html: geofencePopupContent }} />
              <MapPopupActionButton
                onClick={() =>
                  navigate(`/geofence/${selectedGeofence.id}/view`, {
                    state: { from: "/devices" },
                  })
                }
                label={t("common.view_geofence", {
                  defaultValue: "View Geofence",
                })}
              />
            </>
          </Popup>
        )}
      </Map>
    </>
  );
});

// Add display name for debugging
DevicesMap.displayName = "DevicesMap";

export default withMapErrorBoundary(DevicesMap, { fallbackHeight: "65vh" });
